import {
  AfterViewInit,
  ChangeDetectionStrategy, ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  HostListener,
  Inject,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { ActionNote } from '@textbook-api-client/lib/interface/action-note.model';
import { Drawing } from '@textbook-api-client/lib/interface/drawing.model';
import { SingleNote } from '@textbook-api-client/lib/interface/single-note.model';
import { TextbookDrawerService, ToolName } from '@textbook-drawer';
import { concatLatestFrom } from '@ngrx/operators';
import { SwalComponent } from '@sweetalert2/ngx-sweetalert2';
import {
  BehaviorSubject,
  combineLatest,
  distinctUntilChanged,
  from,
  Observable,
  of,
  shareReplay,
  Subject,
  Subscription,
  switchMap,
} from 'rxjs';
import { filter, first, map, skip, takeUntil, tap } from 'rxjs/operators';
import {
  TextbookActionsHandler,
  TextbookActionsHandlerToken,
} from '../../../../../external/providers/textbook-actions-handler';
import { GroupInfo, PlayerType } from '../../../../models';
import { TxtbState } from '../../../../state/states';
import { TxtbActionsSpecificTypes } from '../../../../state/txtb-action';
import { InternalSingleNoteState, SaveStatus } from '../../models';
import { ActionNotesService } from '../../services';
import { errorPipe } from '../../../../pipes/errorPipe';
import { INITIAL_OPACITY, INITIAL_SIZE } from '../../../../drawing-utility/drawing-utility.service';
import { CSS_SIZE_UNIT_PX } from "../../../../../external/models/css-consts";
import { MAX_PERCENT } from "../../../../../external/models/consts";
import { ActionDrawerService } from "../../services/action-drawer.service";
import { ActionDrawerApi } from "../../models/action-drawer-api";

export interface DrawerState extends TxtbState {
  actionType: TxtbActionsSpecificTypes.TxtbActionsDrawer;
  drawerState: string;
  textToolState: SingleNote[];
}

const isEraser = (toolName: unknown): toolName is ToolName.Eraser => toolName === 'eraser';
const isShape = (toolName: ToolName|undefined): boolean =>
  !!toolName &&
  [
    ToolName.Rectangle,
    ToolName.Circle,
    ToolName.IsoscelesTriangle,
    ToolName.RectangularTriangle,
    ToolName.Line,
  ].includes(toolName);

@Component({
  selector: 'txtb-drawer',
  templateUrl: './drawer.component.html',
  styleUrls: ['./drawer.component.scss'],
  providers: [TextbookDrawerService, ActionDrawerService],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class DrawerComponent implements OnInit, AfterViewInit, OnDestroy {
  readonly basicColor = '#EC449E';
  readonly activeColor = '#FF7900';
  readonly clearCanvasColor = '#A8A8A8';
  readonly disabledColor = '#6d6d6db3';
  readonly playerTypes = PlayerType;

  @Input('actionId') set action(actionId: string) {
    this._actionId = actionId;
    this.actionId$ubject.next(actionId);
  }

  @Input() actionContentCollapsed = false;
  @Input() contentVisible = true;
  @Input() contentWrapperBehaviour: 'shrink'|'expand'|'full-width' = 'expand';
  @Input() groupInfo!: GroupInfo;
  @Input() minimizeArrowVisible = true;
  @Input() textbookPageId = '';
  @Input() textbookId = '';
  @Input() zoomFeatureAvailable = false;
  @Output() actionDrawerApiInitialized = new EventEmitter<ActionDrawerApi>();
  @Output() contentCollapse = new EventEmitter<boolean>();
  @Output() actionContentWrapperRendered = new EventEmitter<HTMLElement|null>();
  private _drawerActionWrapper!: ElementRef;
  @ViewChild('drawerActionWrapper') set drawerActionWrapper(element: ElementRef) {
    this._drawerActionWrapper = element;
    this.watchForResize(element);
  }

  get drawerActionWrapper() {
    return this._drawerActionWrapper;
  }

  @ViewChild('drawingsNotSaved')
  public readonly drawingsNotSaved!: SwalComponent;

  @ViewChild('actionContentWrapper') set actionContentWrapper(value: ElementRef<HTMLElement>) {
    this.actionContentWrapperRendered.emit(value?.nativeElement || null);
  }

  private readonly footerHeigthSubject = new BehaviorSubject<string>(`0${ CSS_SIZE_UNIT_PX }`);

  @ViewChild('footer') set footerEl(element: ElementRef) {
    this.footerHeigthSubject.next(`${ !!element ? element.nativeElement.offsetHeight : 0 }${ CSS_SIZE_UNIT_PX }`);
  }

  private readonly headerHeigthSubject = new BehaviorSubject<string>(`0${ CSS_SIZE_UNIT_PX }`);

  @ViewChild('header') set headerEl(element: ElementRef) {
    this.headerHeigthSubject.next(`${ !!element ? element.nativeElement.offsetHeight : 0 }${ CSS_SIZE_UNIT_PX }`);
  }

  readonly headerHeight$: Observable<string> = this.headerHeigthSubject.asObservable();
  readonly footerHeight$: Observable<string> = this.footerHeigthSubject.asObservable();
  @HostBinding('class.touch-actions-disabled') browserTouchActionsDisabled = false;

  // tslint:disable-next-line:variable-name
  readonly MAX_PERCENT = MAX_PERCENT;
  private _actionId = '';
  private accessId!: number;
  private readonly actionId$ubject = new BehaviorSubject('');
  backgroundStyles = {};
  readonly color$ubject = new BehaviorSubject('#000000');
  private currentDrawing: Drawing|undefined;
  private currentActionNote: ActionNote|undefined;
  private readonly destroyTriggerSubject$ = new Subject<void>();
  private isReadonly = true;
  isPremium = false;
  private nextGroupActionId: string|undefined;
  playerType = PlayerType.Textbooks;
  readonly editorPreviewMode = this.textbookActionsHandler.isEditorPreviewMode;
  private readonly saveDrawingsStatus$ = new BehaviorSubject<SaveStatus>(SaveStatus.Create);
  private readonly savingDrawings$ubject = new Subject<void>();
  private readonly savingActionNote$ubject = new Subject<void>();
  readonly size$ubject = new BehaviorSubject(INITIAL_SIZE);
  readonly spinnerVisibility$ubject = new Subject<boolean>();
  private readonly subscription = new Subscription();
  private readonly textboxesActive$ubject = new BehaviorSubject(false);
  readonly textboxesActive$ = this.textboxesActive$ubject.asObservable();
  readonly textToolActive$ubject = new BehaviorSubject(false);
  textToolActive = false;
  readonly tool$uject = new BehaviorSubject<ToolName|undefined>(undefined);
  readonly tool$ = this.tool$uject
    .pipe(shareReplay({ refCount: true, bufferSize: 1 }))
    .pipe(tap((tool) => (this.browserTouchActionsDisabled = !!tool)));
  public readonly drawingByPointerMove$ = this.tool$uject.asObservable().pipe(
    distinctUntilChanged(),
    map((tool) => this.isDrawingByPointerAvailable(tool))
  );
  //TODO Revert change after app manual tests and approval
  readonly zoomDisabled$ = of(true);

  readonly toolName = ToolName;
  readonly transparency$ubject = new BehaviorSubject(INITIAL_OPACITY);
  readonly visibility$ubject = new BehaviorSubject(true);
  private readonly subscribeEvent = new Subscription();
  private resizeObserver?: ResizeObserver;
  private readonly viewHeightBS = new BehaviorSubject<string|null>(null);
  private readonly percentageZoomBS = new BehaviorSubject<number>(MAX_PERCENT);
  readonly vh$ = this.viewHeightBS.asObservable();
  readonly isReadonlyMode$ = this.textbookActionsHandler.isReadonlyMode$;
  readonly percentageZoom$ = this.percentageZoomBS.asObservable();
  readonly resetZoom$ = this.actionDrawerService.resetZoom$;
  readonly isZoomed$ = this.percentageZoom$.pipe(map((percentageZoom) => MAX_PERCENT !== percentageZoom));

  readonly canvasEdited$ = combineLatest([this.textbookDrawerService.stateEdited$, this.actionNotesService.notesEdited$])
    .pipe(map(([stateEdited, notesEdited]) => stateEdited || notesEdited));


  constructor(
    private readonly textbookDrawerService: TextbookDrawerService,
    @Inject(TextbookActionsHandlerToken) public readonly textbookActionsHandler: TextbookActionsHandler,
    private readonly route: ActivatedRoute,
    public readonly actionNotesService: ActionNotesService,
    private readonly actionDrawerService: ActionDrawerService,
    private readonly zone: NgZone,
    private readonly cdr: ChangeDetectorRef,
  ) {
  }

  static addTransparencyToHexColor(color: string, transparency: number): string {
    /** Zapisując kolor w systemie heksadecymalnym 7 i 8 znak (licząc bez hasha) może określać przeźroczystość */
    return color + Math.floor(transparency * 255).toString(16);
  }

  static calculateEraserSize(pencilSize: number): number {
    return 16.67 * pencilSize;
  }

  @HostListener('window:beforeunload', ['$event'])
  beforeUnloadHandler(_: Event): boolean {
    if (this.textbookActionsHandler.isEditorPreviewMode) {
      return true;
    }
    const doNotShowWarningsIfDataNotSave = localStorage.getItem('drawerWarningModalVisibility');
    return !!doNotShowWarningsIfDataNotSave || (!this.textbookDrawerService.isStateEdited() && !this.actionNotesService.isNotesEditionPresent());
  }

  ngOnInit(): void {
    this.actionNotesService.initWithLoadedNotes([]);
    this.subscription.add(
      this.isReadonlyMode$.subscribe((readonly) => (this.isReadonly = readonly))
    );

    this.subscription.add(
      this.textbookActionsHandler.accessId$.subscribe((id) => (this.accessId = id))
    );

    this.subscription.add(
      this.textbookActionsHandler.isPremiumAccess$.subscribe((access) => (this.isPremium = access))
    );

    this.subscription.add(
      this.textbookActionsHandler.playerType$.subscribe(
        (playerType) => (this.playerType = playerType as PlayerType)
      )
    );

    this.subscription.add(
      this.textbookActionsHandler.currentPageNumber$.subscribe((pageNumber) =>
        this.textbookActionsHandler.pageNumber$ubject.next(pageNumber ?? 0)
      )
    );

    this.subscription.add(
      this.textbookDrawerService.currentState$
        .pipe(
          map((drawerState) => (this.textbookDrawerService.isCanvasClear ? '' : drawerState)),
          skip(2)
        )
        .subscribe((drawerState) => {
          this.textbookActionsHandler.isCanvasDirty$.next(drawerState !== '');
        })
    );

    this.subscription.add(
      this.color$ubject.subscribe((color) =>
        this.textbookDrawerService.setColor(
          DrawerComponent.addTransparencyToHexColor(color, this.transparency$ubject.getValue())
        )
      )
    );

    this.subscription.add(
      this.size$ubject.subscribe((size) =>
        this.textbookDrawerService.setSize(
          isEraser(this.tool$uject.getValue()) ? DrawerComponent.calculateEraserSize(size) : size
        )
      )
    );

    this.subscription.add(
      this.transparency$ubject.subscribe((transparency) =>
        this.textbookDrawerService.setColor(
          this.tool$uject.getValue() === ToolName.Eraser
            ? this.color$ubject.getValue()
            : DrawerComponent.addTransparencyToHexColor(this.color$ubject.getValue(), transparency)
        )
      )
    );

    this.subscription.add(
      this.tool$uject.subscribe((tool) => this.textbookDrawerService.setToolName(tool))
    );

    this.subscription.add(
      this.savingActionNote$ubject
        .pipe(
          switchMap(() =>
            this.actionNotesService.currentNotesState$().pipe(
              map((notesState) => notesState),
              first()
            )
          ),
          concatLatestFrom(() => [
            this.textbookActionsHandler.accessId$,
            this.actionNotesService.getSaveActionNoteStatus$(),
          ]),
          switchMap(([notesContent, accessId, saveStatus]) =>
            this.handleSaveActionNote(notesContent, accessId, saveStatus)
          )
        )
        .subscribe(() => {
          this.tool$uject.next(undefined);
          this.toggleTextToolActive(false);
          this.spinnerVisibility$ubject.next(false);
          this.actionId$ubject.next(this._actionId);
        })
    );

    this.subscription.add(
      this.savingDrawings$ubject
        .pipe(
          switchMap(() =>
            this.textbookDrawerService.currentState$.pipe(
              map((drawerState) => drawerState),
              first()
            )
          ),
          concatLatestFrom(() => [this.textbookActionsHandler.accessId$, this.saveDrawingsStatus$]),
          switchMap(([drawerContent, accessId, saveStatus]) =>
            this.handleSaveDrawing(drawerContent, accessId, saveStatus)
          )
        )
        .subscribe(() => {
          this.tool$uject.next(undefined);
          this.textToolActive$ubject.next(false);
          this.spinnerVisibility$ubject.next(false);
          this.actionId$ubject.next(this._actionId);
        })
    );

    this.subscription.add(
      this.actionId$ubject
        .pipe(
          switchMap((actionId: string) => {
            return combineLatest([
              this.textbookActionsHandler.getActionDrawing$(actionId).pipe(errorPipe),
              this.textbookActionsHandler.getActionNote$(actionId).pipe(errorPipe),
            ]).pipe(
              map(([drawing, actionNote]) => {
                const drawingContent = this.setCurrentDrawing(drawing);
                this.initWithNote(actionNote);
                return { drawingContent };
              }),
              tap(() => {
                this.setSaveStatus();
                this.setActionNoteSaveStatus();
              })
            );
          })
        )
        .subscribe(({ drawingContent }) =>
          this.textbookDrawerService.load(drawingContent ? drawingContent.content : ''))
    );
    this.textToolActive$ubject
      .pipe(takeUntil(this.destroyTriggerSubject$))
      .subscribe((active) => (this.textToolActive = active));
  }

  ngAfterViewInit(): void {
    this.actionDrawerApiInitialized.next(this.actionDrawerService);
  }

  onDrawingAreaTouchStart(event: TouchEvent): void {
    if (!!this.tool$uject.getValue()) {
      event.preventDefault();
    }
  }

  setCurrentDrawing(drawing?: Drawing): Drawing|undefined {
    this.currentDrawing = drawing;
    return this.currentDrawing;
  }

  initWithNote(actionNote?: ActionNote): ActionNote|undefined {
    this.currentActionNote = actionNote;
    this.actionNotesService.initWithLoadedNotes(
      actionNote
        ? actionNote.notes.map((note) => ({
          id: Math.random().toString(),
          note,
        }))
        : []
    );
    return this.currentActionNote;
  }

  handleSaveDrawing(
    content: string,
    accessId: number,
    saveStatus: SaveStatus
  ): Observable<Drawing|any> {
    const drawingId = this.currentDrawing ? this.currentDrawing.id : '';

    return {
      create: this.textbookActionsHandler.createDrawing$(accessId, this._actionId, content),
      update: this.textbookActionsHandler.updateDrawing$(drawingId, accessId, content),
      delete: this.textbookActionsHandler.deleteDrawing$(drawingId, accessId),
    }[saveStatus];
  }

  handleSaveActionNote(
    content: InternalSingleNoteState[],
    accessId: number,
    saveStatus: SaveStatus
  ): Observable<ActionNote|any> {
    const notes = content.map((note) => note.note);
    const actionNoteId = this.currentActionNote ? this.currentActionNote.id : '';

    return {
      create: this.textbookActionsHandler.createActionNote$(accessId, this._actionId, notes),
      update: this.textbookActionsHandler.updateActionNote$(actionNoteId, accessId, notes),
      delete: this.textbookActionsHandler.deleteActionNote$(actionNoteId, accessId),
    }[saveStatus];
  }

  toggleVisibility(): void {
    const visible = this.visibility$ubject.getValue();

    if (visible) {
      this.tool$uject.next(undefined);
    }

    this.textbookDrawerService.setLocked(visible);
    this.visibility$ubject.next(!visible);
  }

  color(color: string): void {
    this.color$ubject.next(color);
  }

  size(size: number): void {
    this.size$ubject.next(size);
  }

  transparency(transparency: number): void {
    this.transparency$ubject.next(transparency);
  }

  clear(): void {
    if (
      this.saveDrawingsStatus$.getValue() === SaveStatus.Create &&
      this.actionNotesService.saveActionNoteStatus === SaveStatus.Create
    ) {
      this.textbookDrawerService.clear();
      this.actionNotesService.clear();
      return;
    }
    this.saveDrawingsStatus$.next(SaveStatus.Delete);
    this.actionNotesService.setSaveActionNoteStatus(SaveStatus.Delete);
    this.showDrawer();
    this.tool$uject.next(undefined);
    this.toggleTextToolActive(false);
    this.textbookDrawerService.clear();
    this.actionNotesService.clear();
    localStorage.setItem(this._actionId, '');
  }

  setSaveStatus(): void {
    this.saveDrawingsStatus$.next(this.currentDrawing ? SaveStatus.Update : SaveStatus.Create);
  }

  setActionNoteSaveStatus(): void {
    this.actionNotesService.setSaveActionNoteStatus(
      this.currentActionNote ? SaveStatus.Update : SaveStatus.Create
    );
  }

  toggleTool(tool: ToolName): void {
    this.showPopupIfOffline();
    this.showDrawer();
    this.setSaveStatus();
    this.toggleTextToolActive(false);

    if (tool === this.tool$uject.getValue()) {
      this.tool$uject.next(undefined);
      return;
    }

    if (isEraser(tool)) {
      this.textbookDrawerService.setColor(this.color$ubject.getValue());
      this.textbookDrawerService.setSize(
        DrawerComponent.calculateEraserSize(this.size$ubject.getValue())
      );
      this.tool$uject.next(tool);

      return;
    }

    /** Zapisując kolor w systemie heksadecymalnym 7 i 8 znak (licząc bez hasha) może określać przeźroczystość */
    this.textbookDrawerService.setColor(
      DrawerComponent.addTransparencyToHexColor(
        this.color$ubject.getValue(),
        this.transparency$ubject.getValue()
      )
    );
    this.textbookDrawerService.setSize(this.size$ubject.getValue());
    this.tool$uject.next(tool);
  }

  toggleTextTool(): void {
    this.isZoomed$.pipe(first(), filter((zoomed) => !zoomed)).subscribe(() => {
      this.showPopupIfOffline();
      this.showDrawer();
      this.setActionNoteSaveStatus();
      this.toggleTextToolActive(!this.textToolActive);
      this.tool$uject.next(undefined);
    });
  }

  toggleTextToolActive(isActive: boolean): void {
    this.textToolActive = isActive;
    this.textToolActive$ubject.next(isActive);
  }

  toggleContentCollapse(): void {
    this.actionContentCollapsed = !this.actionContentCollapsed;
    this.contentCollapse.emit(this.actionContentCollapsed);
  }

  toggleTextboxesActiveState(active: boolean): void {
    this.textboxesActive$ubject.next(active);
  }

  showPopupIfOffline(): void {
    if (this.isReadonly) {
      return this.textbookActionsHandler.isActionRestrictedInOffline$ubject.next(true);
    }
  }

  onSaveClick(): void {
    this.showPopupIfOffline();
    this.canvasEdited$.pipe(first()).subscribe((cavasEdited) => {
      if (!cavasEdited || this.isReadonly) {
        return;
      }
      if (!this.isPremium) {
        this.textbookActionsHandler.isBasicUserModalOpen$ubject.next(true);
        return;
      }

      this.spinnerVisibility$ubject.next(true);
      this.savingDrawings$ubject.next();
      this.savingActionNote$ubject.next();
    });
  }

  showDrawer(): void {
    const visible = this.visibility$ubject.getValue();
    if (!visible) {
      this.textbookDrawerService.setLocked(visible);
      this.visibility$ubject.next(!visible);
    }
  }

  showModalIfDrawingsNotSaved(actionId?: string): void {
    this.nextGroupActionId = actionId;
    this.actionDrawerService.resetZoom();
    this.confirmRedirect();
  }

  confirmRedirect(): void {
    this.nextGroupActionId
      ? this.textbookActionsHandler.redirectToAction(this.nextGroupActionId, this.route)
      : this.textbookActionsHandler.backToTextbook(this.route);
  }

  ngOnDestroy(): void {
    this.toggleTextToolActive(false);
    this.tool$uject.next(undefined);
    this.subscription.unsubscribe();
    this.subscribeEvent.unsubscribe();
    this.headerHeigthSubject.complete();
    this.footerHeigthSubject.complete();
    this.tool$uject.complete();
    this.resizeObserver?.disconnect();
    this.viewHeightBS.complete();
  }

  canDeactivate(): Observable<boolean> {
    const doNotShowWarningsIfDataNotSave = localStorage.getItem('drawerWarningModalVisibility');
    return this.canvasEdited$.pipe(first(), switchMap((canvasEdited) => {
      if (
        canvasEdited &&
        this.playerType === this.playerTypes.Textbooks &&
        !doNotShowWarningsIfDataNotSave &&
        this.isPremium &&
        !this.editorPreviewMode
      ) {
        return from(this.drawingsNotSaved.fire()).pipe(
          tap((result) => {
            if (result.value) {
              localStorage.setItem('drawerWarningModalVisibility', result.value);
            }
          }),
          map((result) => result.isConfirmed),
          first()
        );
      }
      return of(true);
    }));
  }

  onZoomChange(percentageZoom: number): void {
    if (this.percentageZoomBS.getValue() === percentageZoom) {
      return;
    }
    if (MAX_PERCENT !== this.percentageZoomBS.getValue()) {
      this.percentageZoomBS.next(percentageZoom);
      return;
    }
    if (MAX_PERCENT === this.percentageZoomBS.getValue() && MAX_PERCENT !== percentageZoom) {
      this.cdr.detach();
      this.percentageZoomBS.next(percentageZoom);
      this.cdr.detectChanges();
      this.cdr.reattach();
    }
  }

  private watchForResize(element?: ElementRef): void {
    this.resizeObserver?.disconnect();
    if (!element) {
      this.viewHeightBS.next(null);
      return;
    }
    this.zone.runOutsideAngular(() => {
      this.resizeObserver = new ResizeObserver(() => {
        const vh = `${ window.innerHeight * 0.01 }${ CSS_SIZE_UNIT_PX }`;
        if (this.viewHeightBS.getValue() !== vh) {
          this.viewHeightBS.next(vh);
        }
      });
      this.resizeObserver.observe(element.nativeElement);
    });
  }

  private isDrawingByPointerAvailable(tool: ToolName|undefined): boolean {
    if (!tool) {
      return false;
    }
    return ToolName.Brush === tool || ToolName.Eraser === tool || isShape(tool);
  }
}
